import { CircleHelp, Search } from "lucide-react";

import { Breadcrumb } from "@/app/shell/Breadcrumb";
import { QuickCreateMenu } from "@/app/shell/QuickCreateMenu";
import { UserMenu } from "@/app/shell/UserMenu";
import { useT } from "@/shared/i18n/useT";
import { MOD_KEY } from "@/shared/lib/platform";
import { Kbd } from "@/shared/ui/Kbd";
import { Tooltip } from "@/shared/ui/Tooltip";

interface DesktopHeaderProps {
  onSearch: () => void;
  onShowShortcuts: () => void;
  onShowAbout: () => void;
}

/**
 * The bar above the work panel. Three zones on one baseline: where you are (start), search
 * everything (centre), and your actions (end). It stays put while the panel scrolls.
 */
export function DesktopHeader({ onSearch, onShowShortcuts, onShowAbout }: DesktopHeaderProps) {
  const t = useT();
  return (
    <header className="hidden h-14 shrink-0 grid-cols-[minmax(0,1fr)_minmax(18rem,30rem)_minmax(0,1fr)] items-center gap-4 ps-1 pe-0.5 pb-2 lg:grid">
      <Breadcrumb />
      <button
        type="button"
        onClick={onSearch}
        className="group/search flex h-10 w-full items-center gap-2.5 rounded-xl border border-line bg-surface ps-3.5 pe-2 text-[14px] text-ink-subtle shadow-xs transition-[border-color,box-shadow,color] hover:border-line-strong hover:text-ink-muted hover:shadow-sm focus-visible:ring-3 focus-visible:ring-brand-ring focus-visible:outline-none"
      >
        <Search
          className="size-4 shrink-0 text-ink-faint transition-colors group-hover/search:text-brand-strong"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-start">{t("shell.search")}</span>
        <span dir="ltr" className="flex shrink-0 gap-0.5">
          <Kbd>{MOD_KEY}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className="flex items-center justify-end gap-1.5">
        <Tooltip label={t("shell.shortcuts")} side="bottom" align="end">
          <button
            type="button"
            onClick={onShowShortcuts}
            aria-label={t("shell.shortcuts")}
            className="grid size-9 place-items-center rounded-xl text-ink-muted transition-colors hover:bg-surface hover:text-ink hover:shadow-xs focus-visible:ring-3 focus-visible:ring-brand-ring focus-visible:outline-none"
          >
            <CircleHelp className="size-[18px]" aria-hidden="true" />
          </button>
        </Tooltip>
        <QuickCreateMenu />
        <span aria-hidden="true" className="mx-1.5 h-6 w-px bg-line-strong" />
        <UserMenu placement="header" onShowShortcuts={onShowShortcuts} onShowAbout={onShowAbout} />
      </div>
    </header>
  );
}
